import { createServerFn } from "@tanstack/react-start";
import snapshot from "@/data/central-bank-gold-snapshot.json";
import {
  buildCentralBankGold,
  imfDataUrl,
  isUsableDataset,
  parseImfCsv,
  type CbgDTO,
} from "@/lib/central-bank-gold";

// IMF IRFCL updates monthly, so a 12 hour cache per isolate is plenty. On any
// failure the last good response (in memory, then the stored snapshot) is served.
const TTL = 12 * 60 * 60 * 1000;
const RETRY_AFTER_FAILURE = 30 * 60 * 1000;

let cache: { at: number; data: CbgDTO } | null = null;
let lastFailure = 0;

const stored = snapshot as CbgDTO;

export const getCentralBankGold = createServerFn({ method: "GET" }).handler(async (): Promise<CbgDTO> => {
  const now = Date.now();
  if (cache && now - cache.at < TTL) return cache.data;
  if (now - lastFailure < RETRY_AFTER_FAILURE) return cache?.data ?? stored;
  try {
    const res = await fetch(imfDataUrl(new Date(now)), {
      headers: { Accept: "application/vnd.sdmx.data+csv;version=1.0.0" },
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) throw new Error(`IMF HTTP ${res.status}`);
    const data = buildCentralBankGold(parseImfCsv(await res.text()), new Date(now).toISOString(), "live");
    if (!isUsableDataset(data)) throw new Error("IMF dataset incomplete");
    // Never step backwards behind the stored snapshot.
    if (data.latestPeriod < stored.latestPeriod) throw new Error("IMF response older than snapshot");
    cache = { at: now, data };
    return data;
  } catch (err) {
    lastFailure = now;
    console.error("central bank gold read failed", err);
    return cache?.data ?? stored;
  }
});
